import { FieldDef } from '../../types/forms';
import { z } from 'zod';
import dayjs from 'dayjs';

export const buildZod = (defs: FieldDef[]) => {
    const shape: Record<string, z.ZodTypeAny> = {};

    defs.forEach((f) => {
        let schema: z.ZodTypeAny;

        if (f.type === 'number') {
            schema = z.any();
            if (!f.required) {
                schema = schema.optional();
            }
        } else if (f.type === 'email') {
            schema = z.string().email();
            if (!f.required) {
                schema = schema.optional();
            }
        } else if (f.type === 'date') {
            schema = z.any().refine(
                (v) => {
                    if (!f.required && (
                        v === null || v === undefined || v === ''
                    )) {
                        return true;
                    }
                    return dayjs(v as any).isValid();
                },
                'Invalid date',
            );
            if (!f.required) {
                schema = schema.optional();
            }
        } else if (f.type === 'radio' || f.type === 'select') {
            schema = z.string().or(z.number()).or(z.boolean()).or(z.array(z.string()).optional());
            if (!f.required) {
                schema = schema.optional();
            }
        } else if (f.type === 'color') {
            schema = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Invalid color');
            if (!f.required) {
                schema = schema.optional();
            }
        } else if (f.type === 'image' || f.type === 'video' || f.type === 'document') {
            schema = z.string().or(
                z.any().refine((v) => typeof File !== 'undefined' && v instanceof File, 'Invalid file'),
            );

            if (f.required) {
                schema = schema.refine(
                    (v) => {
                        if (typeof v === 'string') {
                            return v.trim().length > 0;
                        }
                        return typeof File !== 'undefined' && v instanceof File;

                    },
                    'File is required',
                );
            } else {
                schema = schema.optional();
            }
        } else {
            let base = z.string();
            if (f.pattern) {
                base = base.regex(new RegExp(f.pattern));
            }
            if (typeof f.min === 'number') {
                base = base.min(f.min);
            }
            if (typeof f.max === 'number') {
                base = base.max(f.max);
            }
            schema = base;
            if (!f.required) {
                schema = schema.optional();
            }
        }
        shape[f.id] = schema;
    });
    return z.object(shape);
};

export const getNameFromUrl = (url: string) => {
    const clean = url.split('?')[0].split('#')[0];
    const last = clean.split('/').pop() || url;
    return decodeURIComponent(last);
};

export const isImageUrl = (url: string) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url.split('?')[0].split('#')[0]);

export const isVideoUrl = (url: string) => /\.(mp4|webm|ogg|mov|m4v)$/i.test(url.split('?')[0].split('#')[0]);


export const findSelectActiveGroupOption = (f: FieldDef, sel: any): string | undefined => {
    return f.options?.find((o: any) => String(o.value) === String(sel))?.group || undefined;
};

export const findNumberActiveGroupOption = (f: FieldDef, sel: any): string | undefined => {
    let group: string | undefined;
    if (!f.constraints) {
        return group;
    }
    f.constraints.forEach((c: any) => {
        switch (c.condition) {
            case 'is less than':
                if (parseFloat(sel) < parseFloat(c.compareTo)) {
                    group = c.group;
                }
                return;
            case 'is equal to':
                if (parseFloat(sel) === parseFloat(c.compareTo)) {
                    group = c.group;
                }
                return;
            case 'is not equal to':
                if (parseFloat(sel) !== parseFloat(c.compareTo)) {
                    group = c.group;
                }
                return;
            case 'is higher than':
                if (parseFloat(sel) > parseFloat(c.compareTo)) {
                    group = c.group;
                }
                return;
            case 'is between':
                const [ a, b ] = c.compareTo.split('-');
                if (parseFloat(sel) > parseFloat(a) && parseFloat(sel) < parseFloat(b)) {
                    group = c.group;
                }
                return;
        }
    });
    return group;
};


export const filterFunctions = {
    select: findSelectActiveGroupOption,
    radio: findSelectActiveGroupOption,
    number: findNumberActiveGroupOption,
};

export const computeActiveFields = (all: FieldDef[], values: Record<string, any>, shouldSeparateLeaves: boolean): {
    activeFields: FieldDef[],
    activeLeaves: FieldDef[],
} => {
    const indexById = new Map<string, number>();
    all.forEach((f, i) => indexById.set(f.id, i));

    const activeGroups = new Set<string>([ 'root' ]);

    const collectGroupsFromSelected = (f: FieldDef, selected: any) => {
        if (!f.options) {
            return;
        }

        const selectedList = Array.isArray(selected) ? selected : [ selected ];

        for (const sel of selectedList) {
            if (sel === null || sel === undefined || sel === '') {
                continue;
            }

            // @ts-ignore // will add other types if needed later, now works for selects and number
            const filterFunction = filterFunctions[f.type];
            if (!filterFunction) {
                return;
            }
            const group = filterFunction(f, sel);
            if (group) {
                activeGroups.add(String(group));
            }
        }
    };

    for (const f of all) {
        if ((
            f.type === 'select' || f.type === 'radio' || f.type === 'number'
        ) && (
            f.options || f.constraints
        )) {
            collectGroupsFromSelected(f, values[f.id]);
        }
    }

    const activeLeaves: FieldDef[] = [];
    const activeFields = all.filter((f) => {
        const isActive = (
            !f.group || f.group === 'root' || activeGroups.has(String(f.group))
        );
        if (isActive && shouldSeparateLeaves && (
            (
                f.type === 'string' && f.name !== 'This CDT'
            )
            || (
                f.type === 'image' && !f.label
            )
            || f.type === 'widget'
            || f.type === 'url'
        )) {
            activeLeaves.push(f);
            return false;
        }

        return isActive;
    });

    activeFields.sort((a, b) => (
        indexById.get(a.id) ?? 0
    ) - (
        indexById.get(b.id) ?? 0
    ));

    return { activeFields, activeLeaves };
};
